import React, { useEffect, useRef, useState } from "react";
import { InstantReportButton } from "components/reporting/InstantReportButton";
import { AssetService, SelfReportingService, FleetService } from 'services';
import { Intent } from "@blueprintjs/core";
import PropTypes from "prop-types";
import { NotificationToaster } from "components/notifications";
import { Dropdown } from "components/dropdown";
import { Link } from "react-router-dom";

const defaultObject = {};

export function ReportList(
    {
        lastSearchPayload = defaultObject,
        popoverUsePortal = true,
        onReportDownloadingChange = () => { },
        compact = false,
        compactLarge = false,
        disabled = false,
        loading = false,
        pageName
    }
) {
    const reportAttributeMappings = [
        { "type": "Excel", "icon": "fa fa-file-excel-o" },
        { "type": "PDF", "icon": "fa fa-file-pdf-o" },
        { "type": "CSV", "icon": "fa fa-file-code-o" }
    ];

    const [reportTypeList, setReportTypeList] = useState([]);
    const [reportTypes, setReportTypes] = useState([]);
    const [generatingReportInstanceId, setGeneratingReportInstanceId] = useState();
    const [reportDownloading, setReportDownloading] = useState(false);
    const generationCancelTokenSource = useRef();

    useEffect(() => {
        callTachoReports();
    }, []);

    useEffect(() => {
        const reportTypeButtons = reportTypeList.map((x) => {
            return <InstantReportButton buttonType="MenuItem" key={x.id} id={x.id.toString()} text={x.name} icon={<i className={x.icon} ></i>} onReportGenerate={() => onReportGenerate(x.id)} onReportDownloadComplete={onReportDownloadComplete} reportId={generatingReportInstanceId} intent="" />
        });
        setReportTypes(reportTypeButtons);
    }, [lastSearchPayload, reportTypeList]);

    useEffect(() => {
        if (generatingReportInstanceId != null) {
            SelfReportingService.getInstantReport(generatingReportInstanceId, generationCancelTokenSource, notifyResponse);
        }
    }, [generatingReportInstanceId]);

    function callTachoReports() {
        if (pageName === "Asset-Events" || pageName === "Cost-Listing" || pageName === "Defect-Listing") {
            FleetService.getFleetReports(pageName).then((response) => {
                setReportTypeList(formatReportTypes(response));
            }).catch((error) => {
                NotificationToaster.show(Intent.DANGER, `Failed to get Report Types. ${error}`);
            });
        } else if (pageName === "FMS") {
            Promise.all([
                FleetService.getFleetReports(pageName),
                AssetService.getTachoReports(pageName)
            ]).then(([fleetReports, assetReports]) => {
                // Prefix IDs to avoid clashes
                const fleetReportsWithPrefix = fleetReports.map(r => ({ ...r, id: `fleet-${r.id}` }));
                const assetReportsWithPrefix = assetReports.map(r => ({ ...r, id: `asset-${r.id}` }));
                setReportTypeList(formatReportTypes([...fleetReportsWithPrefix, ...assetReportsWithPrefix]));
            }).catch((error) => {
                NotificationToaster.show(Intent.DANGER, `Failed to get Report Types. ${error}`);
            });
        } else {
            AssetService.getTachoReports(pageName).then((response) => {
                setReportTypeList(formatReportTypes(response));
            }).catch((error) => {
                NotificationToaster.show(Intent.DANGER, `Failed to get Report Types. ${error}`);
            });
        }
    }

    function formatReportTypes(types) {
        return types.map((x) => {
            const mappingItem = reportAttributeMappings.find(y => y.type === x.type);
            return { "id": x.id, "name": x.name, "icon": mappingItem.icon };
        });
    }

    function notifyResponse(response) {
        onReportDownloadComplete();
        if (response) {
            NotificationToaster.show(
                Intent.SUCCESS,
                "Report has been generated successfully, download will now begin"
            );
        }
        else {
            NotificationToaster.show(
                Intent.DANGER,
                "Could not generate the report. Please try again.");
        }
    }

    function onReportDownloadComplete() {
        setGeneratingReportInstanceId(null);
        setReportDownloading(false);
        onReportDownloadingChange(false, null);
    }

    function onReportGenerate(reportId) {
        setReportDownloading(true);
        let actualId = reportId;
        let source = null;
        if (typeof reportId === 'string' && reportId.startsWith('fleet-')) {
            actualId = reportId.replace('fleet-', '');
            source = 'fleet';
        } else if (typeof reportId === 'string' && reportId.startsWith('asset-')) {
            actualId = reportId.replace('asset-', '');
            source = 'asset';
        }
        const report = reportTypeList.find(r => r.id === reportId);
        const reportName = report ? report.name : null;
        onReportDownloadingChange(true, reportName);

        if (
            pageName === "Asset-Events" || 
            pageName === "Cost-Listing" || 
            pageName === "Defect-Listing"
        ) {
            const appliedFilters = lastSearchPayload.appliedFilters || [];
            const selectedLocations = lastSearchPayload.selectedLocations || [];
            const filters = [
                ...appliedFilters.map(f => ({ key: f.name, value: String(f.value) })),
                ...(
                    Array.isArray(selectedLocations)
                        ? selectedLocations.map(loc => ({ key: "Location", value: String(loc) }))
                        : []
                ),
                ...(lastSearchPayload.assetId ? [{ key: "Asset_ID", value: String(lastSearchPayload.assetId) }] : [])
            ];
            const payload = {
                startDate: lastSearchPayload.startDate,
                endDate: lastSearchPayload.endDate,
                searchTerm: lastSearchPayload.searchTerm ?? '',
                Filters: filters
            };

            FleetService.requestAssetEventsReport(pageName, actualId, payload).then((result) => {
                setGeneratingReportInstanceId(result);
            }, (error) => {
                NotificationToaster.show(Intent.DANGER, `Failed to download report. ${error}`);
                onReportDownloadComplete();
            });
        } else if (source === 'fleet') {
            const payload = {
                startDate: lastSearchPayload.startDate,
                endDate: lastSearchPayload.endDate,
                searchTerm: lastSearchPayload.searchTerm ?? '',
                filters: lastSearchPayload.filters
            };

            FleetService.requestAssetEventsReport(pageName, actualId, payload)
                .then((result) => { setGeneratingReportInstanceId(result); }, (error) => {
                    NotificationToaster.show(Intent.DANGER, `Failed to download report. ${error}`);
                    onReportDownloadComplete();
                });
        } else {
            const payload = normaliseExtractFilters(lastSearchPayload, reportName);
            AssetService.requestTachoReport(actualId, pageName, payload).then((result) => {
                setGeneratingReportInstanceId(result);
            }, (error) => {
                NotificationToaster.show(Intent.DANGER, `Failed to download report. ${error}`);
                onReportDownloadComplete();
            });
        }
    }

    function normaliseExtractFilters(payload, reportName) {
        if (!payload || !payload.filters || reportName !== "Fleet List Extract") {
            return payload;
        }
        return {
            ...payload,
            filters: payload.filters.map(f => {
                if (f.key === "ASSETOWNERSHIPID") {
                    return { ...f, key: "Ownership" };
                }
                if (f.key === "ASSETSTATUSID") {
                    return { ...f, key: "Status" };
                }
                return f;
            })
        };
    }

    const reportDropdownPreamble = (
        <>
            <h4>Downloads</h4>
            <p>
                For reports requiring date ranges only the data shown on the calendar will be included.<br /> For more detailed reporting visit <Link to='/self-service-reports'>Self-Service Reports</Link>
            </p>
        </>
    );
    return (
        <Dropdown intent="primary" text={compact || compactLarge ? "" : "Reports"} icon={compact || compactLarge ? "document" : null} large={!compact} iconOnly={compact || compactLarge} id="reports" items={reportTypes} preamble={reportDropdownPreamble} disabled={reportDownloading || disabled} popoverUsePortal={popoverUsePortal}
            loading={loading} />
    );
}

ReportList.propTypes = {
    lastSearchPayload: PropTypes.object,
    pageName: PropTypes.string,
    popoverUsePortal: PropTypes.bool,
    onReportDownloadingChange: PropTypes.func,
    compact: PropTypes.bool,
    compactLarge: PropTypes.bool,
    disabled: PropTypes.bool,
    loading: PropTypes.bool
};